import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import type { DevelopmentDetailSolicitationResponse } from '@/domain/models/development.js';
import type { SolicitationEvidenceProps } from '@/domain/models/solicitation-evidence.js';
import { ConfirmDialog } from '@/presentation/components/index.js';
import { JiraSolicitationStatus } from '@/presentation/pages/developments/development-detail/components/jira-solicitation-status.js';
import { SolicitationEvidenceList } from '@/presentation/pages/developments/solicitations/solicitation-evidence-list.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import type { TranslateFn } from '@/shared/utils/format-date.js';

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'default'> = {
    ABERTA: 'warning',
    RESOLVIDA: 'success'
};

interface SolicitationCardProps {
    solicitation: DevelopmentDetailSolicitationResponse;
    canManageSolicitations: boolean;
    canResolveSolicitation: boolean;
    canEvaluateSolicitation: boolean;
    actionLoading: { approving: boolean };
    onApprove: (id: string) => void;
    onReject: (id: string) => void;
    onReclassify: (id: string) => void;
    onResolve: (id: string) => void;
    fetchEvidences: (solicitationId: string) => Promise<SolicitationEvidenceProps[]>;
    onDownloadEvidence: (solicitationId: string, docId: string) => void;
    onRemoveEvidence: (solicitationId: string, docId: string) => Promise<boolean>;
    translate: TranslateFn;
    evidenceLabels: {
        attachments: string;
        downloadAttachment: string;
        removeAttachment: string;
    };
    removeConfirmLabels: {
        title: string;
        confirmText?: string;
        cancelText?: string;
    };
}

export function SolicitationCard({
    solicitation: s,
    canManageSolicitations,
    canResolveSolicitation,
    canEvaluateSolicitation,
    actionLoading,
    onApprove,
    onReject,
    onReclassify,
    onResolve,
    fetchEvidences,
    onDownloadEvidence,
    onRemoveEvidence,
    translate,
    evidenceLabels,
    removeConfirmLabels
}: SolicitationCardProps) {
    const [evidences, setEvidences] = useState<SolicitationEvidenceProps[]>([]);
    const [loadingEvidences, setLoadingEvidences] = useState(true);
    const [evidencesError, setEvidencesError] = useState(false);
    const [evidenceToRemove, setEvidenceToRemove] = useState<string | null>(null);
    const [removingEvidence, setRemovingEvidence] = useState(false);

    useEffect(() => {
        let active = true;
        setLoadingEvidences(true);
        fetchEvidences(s.id)
            .then((list) => {
                if (active) {
                    setEvidences(list);
                }
            })
            .catch(() => {
                if (active) {
                    setEvidencesError(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoadingEvidences(false);
                }
            });
        return () => {
            active = false;
        };
    }, [s.id, fetchEvidences]);

    const handleConfirmRemoveEvidence = async () => {
        if (!evidenceToRemove) {
            return;
        }
        setRemovingEvidence(true);
        try {
            const removed = await onRemoveEvidence(s.id, evidenceToRemove);
            if (removed) {
                setEvidences((current) => current.filter((e) => e.id !== evidenceToRemove));
                setEvidenceToRemove(null);
            }
        } finally {
            setRemovingEvidence(false);
        }
    };

    const handleCloseRemoveDialog = () => {
        if (!removingEvidence) {
            setEvidenceToRemove(null);
        }
    };

    const canRemoveEvidence = s.status === 'ABERTA' && canManageSolicitations;
    const showEvidenceSection = loadingEvidences || evidencesError || evidences.length > 0;

    return (
        <Box data-testid={`solicitation-card-${s.id}`} sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={translate(`requestTypeLabels.${s.type}`)} size="small" />
                <Chip label={translate(`requestStatusLabels.${s.status}`)} size="small" color={STATUS_COLORS[s.status] ?? 'default'} variant="outlined" />
            </Stack>
            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                {s.title}
            </Typography>
            <JiraSolicitationStatus
                status={s.jiraSync?.status ?? 'NONE'}
                sendingLabel={translate('devDetail.jiraSolicitationSending')}
                retryLabel={jiraRetryLabel(translate, s.jiraSync?.nextAttemptAt ?? null)}
            />
            <Typography variant="caption" color="text.secondary">
                {s.description}
            </Typography>
            {s.steps && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {translate('solicitations.stepsToReproduce')}: {s.steps}
                </Typography>
            )}
            {s.ltResponse && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {translate('solicitations.techLeadResponse')}: {s.ltResponse}
                </Typography>
            )}
            {s.rejectionNotes && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {translate('solicitations.rejectionNotes')}: {s.rejectionNotes}
                </Typography>
            )}
            {s.reclassifiedType && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {translate('solicitations.reclassifiedAs')}: {translate(`requestTypeLabels.${s.reclassifiedType}`)}
                </Typography>
            )}
            {showEvidenceSection && (
                <Box data-testid={`solicitation-evidences-${s.id}`} sx={{ mt: 1 }}>
                    {loadingEvidences ? (
                        <CircularProgress size={16} />
                    ) : evidencesError ? (
                        <Typography variant="caption" color="error">
                            {translate('solicitations.loadEvidencesError')}
                        </Typography>
                    ) : (
                        <SolicitationEvidenceList
                            evidences={evidences}
                            canRemove={canRemoveEvidence}
                            removing={removingEvidence}
                            onDownload={(docId) => onDownloadEvidence(s.id, docId)}
                            onRemove={(docId) => setEvidenceToRemove(docId)}
                            labels={{ ...evidenceLabels, resolutionBadge: translate('solicitations.resolutionBadge') }}
                        />
                    )}
                </Box>
            )}
            {s.status === 'ABERTA' && canEvaluateSolicitation && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" sx={{ color: 'white' }} onClick={() => onApprove(s.id)} disabled={actionLoading.approving}>
                        {translate('solicitations.approve')}
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => onReject(s.id)}>
                        {translate('solicitations.reject')}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => onReclassify(s.id)}>
                        {translate('solicitations.reclassify')}
                    </Button>
                </Stack>
            )}
            {s.status === 'APROVADA' && canResolveSolicitation && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" sx={{ color: 'white' }} onClick={() => onResolve(s.id)}>
                        {translate('solicitations.resolve')}
                    </Button>
                </Stack>
            )}
            <ConfirmDialog
                open={!!evidenceToRemove}
                title={removeConfirmLabels.title}
                confirmLabel={removeConfirmLabels.confirmText}
                cancelLabel={removeConfirmLabels.cancelText}
                confirmColor="error"
                loading={removingEvidence}
                onConfirm={() => handleConfirmRemoveEvidence()}
                onClose={handleCloseRemoveDialog}
            />
        </Box>
    );
}
